import * as React from "react"
import LocalStorageUtils from "../utils/LocalStorageUtils";
import { SectionContext, FlowContext, EventNames } from "../utils/EventTrackerConstants";
import TreatmentPlanCard from "./TreatmentPlanCard";
import ITreatmentPlanTemplateResponseObj, { ITemplateInfo } from "../models/ITreatmentPlanTemplateResponse";
import { ORION_IMG_URL, toastNotificationAppearance } from "../utils/AppConstants";
import { IPdfTemplates, IConstituentTemplate } from "../models/PdfTemplatesModels";
import { IAppState } from "../models/IAppState";
import { TreatmentPlanActions } from "../actions/TreatmentPlanActions";
import { connect } from "react-redux";
import AppUtils from "../utils/AppUtils";
import { IPrescription } from "@curefit/care-common";
import { OngoingConsultationActions } from "../actions/OngoingConsultationActions";
import { get as _get, isEmpty as _isEmpty } from "lodash"
import AppLoader from "./utils/AppLoader";
import { IPastPrescription } from "../models/prescription/IPastPrescription";


export interface ITreatmentPlanComponentProps {
    name?: string
    widgetName?: string
    listItems: Array<ITreatmentPlanTemplateResponseObj>,
    onListItemClick: (listItem: ITreatmentPlanTemplateResponseObj) => void
    emptyText?: string,
    currentPrescription?: IPrescription | undefined,
    treatmentPlansIdNameMap?: { [key: string]: string } | undefined,
    onUndoApplyTreatmentPlan?: (callback: Function, removedTemplate?: ITemplateInfo) => void,
    removeAppliedTemplate?: (templateId: string, flowContext: string) => void,
    isPastPrescriptionFlow: boolean;
    pastPrescriptions?: IPastPrescription[];
}

type Props = ReturnType<typeof mapStateToProps> & ReturnType<typeof mapDispatchToProps> & ITreatmentPlanComponentProps

const TreatmentPlanComponent: React.FC<Props> = ({
    name,
    widgetName,
    listItems = [],
    onListItemClick,
    emptyText = "Please enter a diagnosis to get Treatment Plan template recommendations",
    fetchConsultationPdfTemplates,
    doctorProfile,
    currentPrescription,
    treatmentPlansIdNameMap,
    removeAppliedTemplate,
    onUndoApplyTreatmentPlan,
    editPrescription,
    isPastPrescriptionFlow,
    pastPrescriptions = []
}) => {

    const section = React.useContext(SectionContext);
    const pastPrescriptionsIdNameMap: any = {};
    pastPrescriptions.forEach(({ referenceId, date }) => pastPrescriptionsIdNameMap[referenceId] = date)

    const onClick = (listItem: any) => {
        LocalStorageUtils.setClickEvents({ value: listItem.displayValue, field: name, widget: widgetName, section: section })
        onListItemClick(listItem)
    }

    const [isLoading, setIsLoading] = React.useState(false)
    const [pdfTemplates, setPdfTemplates] = React.useState<IPdfTemplates>(null)

    React.useEffect(() => {
        // On Mount
        getConsultationPdfTemplates()
    }, []);

    const getConsultationPdfTemplates = () => {
        fetchConsultationPdfTemplates(doctorProfile.id, doctorProfile.agentCenterMapping[0].centerId, doctorProfile.primarySubServiceType.code)
            .then((response: IConstituentTemplate) => {
                const templateIdToNameMap = AppUtils.setPdfTemplatesIdToNameMap(response.constituentTemplates || []);
                const templateNameToDataMap = AppUtils.setPdfTemplateNameToDataMap(response.constituentTemplates || []);
                const responseToSave = { ...response, templateIdToNameMap, templateNameToDataMap };
                setPdfTemplates(responseToSave)
            })
            .catch(() => {
                setPdfTemplates(null)
            });
    }

    React.useEffect(() => {
        setIsLoading(true)
        setTimeout(() => {
            setIsLoading(false)
        }, 500)
    }, [listItems]);

    const renderAppliedTemplates = (flowContext: string, isPastPrescriptionFlow: boolean) => {
        let prescription;
        if (flowContext === EventNames.Edit_Prescription) {
            prescription = editPrescription
        } else if (flowContext === EventNames.Consultation) {
            prescription = currentPrescription
        }
        if (!_isEmpty(prescription) && _get(prescription, "prescriptionTemplateMapping.templateIds") && treatmentPlansIdNameMap) {
            return prescription.prescriptionTemplateMapping.templateIds.map((templateId: string) => {
                let tagName = isPastPrescriptionFlow ? pastPrescriptionsIdNameMap[templateId] : treatmentPlansIdNameMap[templateId];
                if (isPastPrescriptionFlow && !pastPrescriptionsIdNameMap[templateId]) {
                    return <></>;
                } else if (!isPastPrescriptionFlow && !treatmentPlansIdNameMap[templateId]) {
                    return <></>;
                }
                return (
                    <div className="applied-template-tag">
                        <div>{tagName}</div>
                        <img onClick={() => {
                            onUndoApplyTreatmentPlan(() => {
                                removeAppliedTemplate(templateId, flowContext);
                                const deleteSuccessMessage: string = `All fields from ${isPastPrescriptionFlow ? "previous prescription dated" : ""} ${tagName} have been removed`;
                                AppUtils.triggerToastNotification(deleteSuccessMessage, toastNotificationAppearance.success);

                            }, { templateId, templateName: treatmentPlansIdNameMap[templateId] })
                        }} className="remove-applied-template-tag-close" src="/images/icons/remove-tag-blue.svg" />
                    </div>
                );
            })
        }
    }

    const getEmpyText = (emptyTextValue: string, flowContext: string, isPastPrescriptionFlow: boolean): string => {
        let prescription;
        if (flowContext === EventNames.Edit_Prescription) {
            prescription = editPrescription
        } else if (flowContext === EventNames.Consultation) {
            prescription = currentPrescription
        }
        if (_get(prescription, "prescriptionTemplateMapping.templateIds")) {
            emptyTextValue = isPastPrescriptionFlow ? "All previous prescriptions have been added" : "All plans have been added";
        }
        return emptyTextValue;
    }

    return (
        <FlowContext.Consumer>
            {flowContext => {
                return (
                    <>
                        {listItems && listItems.length > 0 &&
                            <div>
                                Based on the diagnosis and medicines & lab tests of your preference we’ve created some templates
                            </div>
                        }
                        <div className="applied-template-tags">
                            <div className="applied-text">{isPastPrescriptionFlow ? "Applied Prescriptions: " : "Applied Plans:  "}</div>{renderAppliedTemplates(flowContext, isPastPrescriptionFlow)}
                        </div>
                        <div className="treatment-plan-items">
                            {(isLoading && !isPastPrescriptionFlow)
                                ? <AppLoader text={"Loading Treatment Plans"} />
                                : Boolean(Array.isArray(listItems) && listItems.length) ?
                                    (listItems.slice(0, 4)).map((listItem: ITreatmentPlanTemplateResponseObj) => {
                                        return (
                                            <TreatmentPlanCard
                                                key={listItem.id}
                                                listItem={listItem}
                                                pdfTemplates={pdfTemplates}
                                                onListItemClick={onClick}
                                            />)
                                    })
                                    : <span className="treatment-plan-error">{getEmpyText(emptyText, flowContext, isPastPrescriptionFlow)}</span>
                            }
                        </div>
                    </>
                )
            }}
        </FlowContext.Consumer>
    );
}

const mapStateToProps = (state: IAppState) => {
    return {
        doctorProfile: state.doctorProfile,
        currentPrescription: state.ongoingConsultation && state.ongoingConsultation.medicalProfile && state.ongoingConsultation.medicalProfile.currentConsultationData,
        editPrescription: state.editPrescription?.medicalProfile?.currentConsultationData,
        treatmentPlansIdNameMap: state.treatmentPlanReducer && state.treatmentPlanReducer.treatmentPlansIdNameMap,
        pastPrescriptions: state.ongoingConsultation && (state.ongoingConsultation.pastPrescriptions as IPastPrescription[])
    }
}

const mapDispatchToProps = (dispatch: any) => {
    return {
        fetchConsultationPdfTemplates: (doctorId: number, centerId: number, doctorType: string): Promise<IConstituentTemplate> => {
            return TreatmentPlanActions.fetchConsultationPdfTemplates(doctorId, centerId, doctorType)
        },
        removeAppliedTemplate: (templateId: string, flowContext: string) => {
            dispatch(OngoingConsultationActions.removeAppliedTemplate(templateId, flowContext));
        }
    }
}

export default connect<ReturnType<typeof mapStateToProps>, ReturnType<typeof mapDispatchToProps>, ITreatmentPlanComponentProps>(
    mapStateToProps,
    mapDispatchToProps
)(TreatmentPlanComponent);